import React, { createContext, useContext } from 'react';
import { Asset } from '@24i/nxg-sdk-photon/src';
import { useDimensions } from '@24i/nxg-sdk-quantum';
import { isPlatformWeb } from '@24i/nxg-core-utils/src';

import { ImageService } from '../../clients/AbstractImageServiceClient';
import { isPhoneForImageSelection } from '../../utils/imageSelectionHelpers';

export type ImageServiceContext = ImageService;

// Create the context with proper typing
const ImageServiceContext = createContext<ImageService | undefined>(undefined);

export interface ImageServiceProviderProps {
    client: ImageService;
    children: React.ReactElement;
}

export const ImageServiceProvider = ({ client, children }: ImageServiceProviderProps) => (
    <ImageServiceContext.Provider
        value={{
            ...client,
        }}
    >
        {children}
    </ImageServiceContext.Provider>
);

export const useImage = () => {
    const context = useContext(ImageServiceContext);
    const { width: windowWidth, height: windowHeight } = useDimensions();

    if (context === undefined) {
        throw new Error('useImage must be used within a ImageServiceProvider');
    }

    return {
        ...context,
        /**
         * Enhanced selectDetailBackgroundImage that dynamically responds to device orientation changes.
         *
         * This follows the AC requirements from the Image Type Definition:
         * - AC8: For desktop, tablet, TV: Background image in 16:9
         * - AC9: For desktop, tablet, TV: If no background, use landscape cover
         * - AC10: For mobile (native) or mobile web in portrait: Background image in 2:3 (portrait)
         * - AC11: For mobile (native) or mobile web in portrait: If only 16:9 background available, use it cropped to 2:3
         * - AC12: For mobile (native) or mobile web in portrait: If no background available, use landscape cover cropped to 2:3
         *
         * For mobile web specifically:
         * - In portrait orientation: use portrait images (like native mobile phones)
         * - In landscape orientation: use landscape images (like tablets/desktop)
         */
        selectDetailBackgroundImage: (asset?: Asset) => {
            // For mobile web, we need to be orientation-aware
            // Use considerTablet=true to get proper orientation-aware behavior
            const isPhone = isPhoneForImageSelection(windowWidth, true);

            // Dynamically determine the image path based on current device type and orientation
            const imagePath = isPhone
                ? asset?.backgroundPortrait || asset?.background || asset?.still || ''
                : asset?.background || asset?.still || '';

            // For non-web platforms, use the original implementation but enhance quality
            const nonWebImagePath = context.selectDetailBackgroundImage(asset, windowWidth);

            // Apply better quality and responsive dimensions
            return context.getImageUrl(
                {
                    imagePath: isPlatformWeb
                        ? imagePath
                        : context.getBetterQualityAsset(nonWebImagePath || ''),
                    ...(isPhone ? { height: windowHeight } : { width: windowWidth }),
                },
                isPhone
                    ? asset?.images?.backgroundPortrait || asset?.images?.background
                    : asset?.images?.background
            );
        },
    };
};

// eslint-disable-next-line react/display-name
export const withImageService = (Component: React.ComponentType) => (props) =>
    (
        <ImageServiceContext.Consumer>
            {(imageServiceContext) => <Component {...props} {...imageServiceContext} />}
        </ImageServiceContext.Consumer>
    );

export default ImageServiceProvider;
